import { merge } from 'lodash';
import type { AnyStateMachine } from 'xstate';
import { validateEmailOnRegEx } from '~/components/inputs/email/email.util';
import { BillingMachine } from '~/machines/billing-selection';
import { ContextSendMatch } from '~/machines/utils/types';
import { LDClient, LDFlags } from '~/providers';
import { DeepPartial } from '~/typings/utils';
import { EMAIL_REGEX } from '~/utils/constants';
import { never } from '~/utils/for-warbler';
import { store } from '~/utils/store';
import { sendToSegment } from '~/utils/tracking';
import { Billing, BillingProps } from './billing.component';

export type BillingContainerProps = {
  micro: ContextSendMatch<BillingMachine>;
  funnel: ContextSendMatch<AnyStateMachine>;
  overrideCopy?: BillingProps['overrideCopy'];
  overrideProps?: DeepPartial<BillingProps>;
};

export const BillingContainer = ({
  micro: { context, matches, send },
  overrideCopy,
  overrideProps,
}: BillingContainerProps) => {
  const props: BillingProps = {
    context: {
      existingPaymentMethods: context.existingPaymentMethods,
      experience: context.billingSelection!,
      stripeToken: context.stripeToken!,
      utility: context.funnelPacket.utility,
      user: context.funnelPacket.user,
      isUcbAvailableAsOption: context.isUcbAvailableAsOption,
      funnelId: store.get('ACTIVE_FUNNEL_ID'),
    },
    error: context.errors.payment,
    flags: {
      showPaymentHoldNotice: context.placeCreditCardHold,
    },
    loading: false,
    next: data => {
      if ('existingPaymentMethodId' in data) {
        send({ type: 'SUBMIT_EXISTING_PAYMENT_METHOD', data });
      } else if ('paymentMethod' in data) {
        send({ type: 'SUBMIT_NEW_SETUP_INTENT', data: data.paymentMethod });
      } else {
        never(data);
      }
    },
    overrideCopy,
    pending: matches('payment.submittingExisting'),
  };

  if (
    LDClient.variation(LDFlags.ffSkipPaymentScreen) &&
    validateEmailOnRegEx(context.funnelPacket.user.email, EMAIL_REGEX)
  ) {
    props.skip = () => {
      send({ type: 'SKIP_PAYMENT', data: { billingSelection: 'ucb' } });
      sendToSegment({
        type: 'payment_method_skipped_for_test_user',
        funnelPacket: context.funnelPacket,
        additional: { email: context.funnelPacket.user.email },
      });
    };
  }

  if (
    context.isUcbAvailableAsOption &&
    LDClient.variation(LDFlags.allowPaymentSkipIfUcbAvailable)
  ) {
    props.skip = () => {
      send({ type: 'SKIP_PAYMENT', data: { billingSelection: 'ucb' } });
    };
  }

  return <Billing {...merge(props, overrideProps)} />;
};
